import { useState } from 'react';
import { useNavigate, useParams, useSearchParams } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { faBoxOpen, faTriangleExclamation } from '@fortawesome/free-solid-svg-icons';
import { receivingLotService } from '@/services/receiving-lot.service';
import { PageHeader } from '@/components/shared/PageHeader';
import { Breadcrumb } from '@/components/shared/Breadcrumb';
import { SearchBar } from '@/components/shared/SearchBar';
import { EmptyState } from '@/components/shared/EmptyState';
import { Pagination } from '@/components/shared/Pagination';
import { SkeletonTable } from '@/components/skeletons/SkeletonTable';
import { useDebounce } from '@/hooks/use-debounce';
import { ROUTES } from '@/routes/routes';

const PAGE_SIZE = 20;

export function ModelBatchEquipmentPage() {
  const { lotId, batchId } = useParams<{ lotId: string; batchId: string }>();
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const page = Number(searchParams.get('page') ?? '1');
  const [searchInput, setSearchInput] = useState(searchParams.get('search') ?? '');
  const debouncedSearch = useDebounce(searchInput, 350);

  const { data, isLoading, isError } = useQuery({
    queryKey: ['model-batch-equipment', lotId, batchId, page, debouncedSearch],
    queryFn: () =>
      receivingLotService.listBatchEquipment(lotId!, batchId!, {
        page,
        pageSize: PAGE_SIZE,
        search: debouncedSearch || undefined,
      }),
    enabled: Boolean(lotId && batchId),
  });

  const setPage = (p: number) => {
    const next = new URLSearchParams(searchParams);
    next.set('page', String(p));
    setSearchParams(next);
  };

  return (
    <div className="space-y-5">
      <Breadcrumb
        segments={[
          { label: 'Receiving Lots', to: ROUTES.OPS_RECEIVING_LOTS },
          {
            label: 'Lot',
            to: lotId ? ROUTES.OPS_RECEIVING_LOT_DETAIL.replace(':id', lotId) : undefined,
          },
          { label: data?.equipmentModelName ?? 'Model Batch' },
        ]}
      />

      <PageHeader
        title={data?.equipmentModelName ?? 'Model Batch'}
        description={
          data ? `${data.total} tool${data.total === 1 ? '' : 's'} in this batch` : undefined
        }
      />

      <SearchBar
        value={searchInput}
        onChange={setSearchInput}
        placeholder="Search asset or serial number..."
      />

      {isLoading && <SkeletonTable rows={8} cols={4} />}

      {isError && (
        <EmptyState
          icon={faTriangleExclamation}
          title="Failed to load equipment"
          description="The Model Batch may not exist, or you may not have access to it."
        />
      )}

      {!isLoading && !isError && data && (
        <>
          {data.data.length === 0 ? (
            <EmptyState icon={faBoxOpen} title="No equipment found" />
          ) : (
            <div className="overflow-x-auto rounded-xl border border-border">
              <table className="w-full text-sm">
                <thead className="bg-muted/50 text-left text-xs uppercase text-muted-foreground">
                  <tr>
                    <th className="px-4 py-3 font-medium">Equipment</th>
                    <th className="px-4 py-3 font-medium">Asset #</th>
                    <th className="px-4 py-3 font-medium">Serial #</th>
                    <th className="px-4 py-3 font-medium">Status</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-border">
                  {data.data.map((eq) => (
                    <tr key={eq.id} className="hover:bg-muted/30">
                      <td className="px-4 py-3 text-foreground">
                        <button
                          onClick={() => navigate(ROUTES.EQUIPMENT_DETAIL.replace(':id', eq.id))}
                          className="rounded text-left hover:text-primary hover:underline focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary"
                          aria-label={`View details for ${eq.equipmentName}`}
                          title="View equipment details"
                        >
                          {eq.equipmentName}
                        </button>
                      </td>
                      <td className="px-4 py-3 font-mono text-xs text-muted-foreground">
                        {eq.assetNumber}
                      </td>
                      <td className="px-4 py-3 font-mono text-xs text-muted-foreground">
                        {eq.serialNumber}
                      </td>
                      <td className="px-4 py-3">
                        <span className="rounded-full bg-primary/10 px-2 py-0.5 text-[11px] font-medium text-primary">
                          {eq.status}
                        </span>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}

          <Pagination
            page={data.page}
            pageSize={data.pageSize}
            total={data.total}
            onPageChange={setPage}
          />
        </>
      )}
    </div>
  );
}
